import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useSelector, useDispatch} from 'react-redux';

import Utility from '../../../../../../util/utilityfunctions';
import {labels} from '../../../../../../util/utils';
import {
	RECIPIENT_LIST,
	RECIPIENTS_SELECTED_LIST,
	TYPE,
	SUBJECT,
	FROM_ENTITY,
	FROM_ENTITY_NOTIFICATION,
	NOTIFICATION_MESSAGE,
	LINK_LABEL,
	LINK,
	FROM_ENGAGEMENT,
	getTypeEmail,
	getTypeNotification
} from './utils/constants';
import normalizeContactList from './utils/normalizeContactList';
import getApiPayload from './utils/getApiPayload';
import getAvailableEntityList from './utils/getAvailableEntityList';

import {clearErrors, raiseJSValidationError, raiseSuccessMessage} from '../../../../../../actions/erroractions';
import {resetBulkSelection, setMessageLog} from '../../../../../../actions/ContactDB';

import OutputText from '@ey/canvascoreservices/OutputText';

import RecipientBulk from './RecipientBulk';
import GenerateEmail from './GenerateEmail';
import GenerateNotification from './GenerateNotification';
import StyledModalContent from './StyledModalContent';
import RadioButtonGroup from '../../../../../CoreWrappers/RadioButtonGroup';
import Form from '@ey/canvascoreservices/Form';
import StyledModalGrid from './StyledModalGrid';
import Modal from '../../../../../CoreWrappers/Modal';
import LoadingIndicator from '../../../../../Common/LoadingIndicator';
import HrAlt from '../../../../../Common/Hr/HrAlt';
import setUserList from './RecipientBulk/setUserList';

const GMForm = ({showModal, hideModal, duplicateMessage}) => {
	const [isLoading, setIsLoading] = useState(false);
	const selectedContacts = useSelector((store) => store.contacts.selected);
	const projectId = useSelector((store) => store.project?.projectId);
	const filtersList = useSelector((store) => store.filterData[`${projectId}#ContactsDB`]);
	const filtersData = useSelector((store) => store.contacts.filters?.filterTypes);
	const contactList = useSelector((store) =>
		store.contacts.entities?.length > 0
			? normalizeContactList(getAvailableEntityList(store.contacts.entities, false))
			: []
	);
	const orgChart = useSelector((store) => store.orgChart);
	const entities = useSelector((store) => store.contacts.displayedEntities);
	const {userId, firstName, lastName} = useSelector(({userPreferences}) => userPreferences);

	const dispatch = useDispatch();

	const isDuplicateMessage = duplicateMessage !== null;

	const typeEmail = getTypeEmail(labels.email);
	const typeNotification = getTypeNotification(labels.notification);

	const defaultValues = {
		[RECIPIENTS_SELECTED_LIST]: selectedContacts,
		[TYPE]: typeEmail,
		[SUBJECT]: '',
		[FROM_ENTITY]: null,
		[FROM_ENTITY_NOTIFICATION]: null,
		[FROM_ENGAGEMENT]: null,
		[NOTIFICATION_MESSAGE]: '',
		[LINK_LABEL]: '',
		[LINK]: ''
	};

	const initialValues = {
		[RECIPIENT_LIST]: contactList,
		...(isDuplicateMessage && duplicateMessage.values ? duplicateMessage.values : defaultValues)
	};

	const schema = {
		[RECIPIENT_LIST]: {required: true},
		[RECIPIENTS_SELECTED_LIST]: {required: false},
		[TYPE]: {required: true},
		[SUBJECT]: {required: (values) => values[TYPE].id === typeEmail.id, maxLength: 100},
		[FROM_ENTITY]: {required: (values) => values[TYPE].id === typeEmail.id},
		[FROM_ENTITY_NOTIFICATION]: {required: (values) => values[TYPE].id === typeNotification.id},
		[FROM_ENGAGEMENT]: {required: (values) => values[TYPE].id === typeNotification.id},
		[NOTIFICATION_MESSAGE]: {required: (values) => values[TYPE].id === typeNotification.id, maxLength: 200},
		[LINK_LABEL]: {required: (values) => values[LINK]?.length !== 0, maxLength: 50},
		[LINK]: {required: (values) => values[LINK_LABEL]?.length !== 0, maxLength: 1000}
	};

	const headingProps = {size: 'L'};

	const dispatchNoContactsSelectedToast = () => {
		dispatch(raiseJSValidationError(labels.mustSelectOne));
	};

	const dispatchSentToast = (message) => {
		dispatch(raiseSuccessMessage(message)).then(() =>
			setTimeout(() => {
				dispatch(clearErrors());
			}, 5000)
		);
	};

	const dispatchResetSelectedContacts = () => {
		dispatch(resetBulkSelection());
	};

	const renderLoading = () => <LoadingIndicator show />;

	const renderInstructionalText = () => {
		return (
			<OutputText light {...props}>
				{labels.gmInstructionalText}
			</OutputText>
		);
	};

	const props = {
		initialValues,
		schema,
		labels: {
			required: labels.required,
			maxLength: labels.formLengthMessage
		},
		onSubmit: (values, callback) => {
			setIsLoading(true);

			const recipientList = setUserList(values);

			if (recipientList.length === 0) {
				dispatchNoContactsSelectedToast();
				setIsLoading(false);
			}

			if (recipientList.length > 0) {
				let handleAfterMessageLog;
				const recipients = recipientList.map(({emailAddress, userId, name, lastName}) => ({
					emailAddress,
					contactId: userId.toString(),
					firstName: name,
					lastName
				}));

				if (values[TYPE].id === typeEmail.id) {
					handleAfterMessageLog = () => {
						Utility.sendEmails(recipients.map(({emailAddress}) => emailAddress).join(';'), values[SUBJECT]);
						setIsLoading(false);
						callback();
						dispatchSentToast(labels.emailSentMsg);
						dispatchResetSelectedContacts();
					};
				} else {
					handleAfterMessageLog = () => {
						setIsLoading(false);
						callback();
						dispatchSentToast(labels.notificationSentMsg);
						dispatchResetSelectedContacts();
					};
				}

				const payload = getApiPayload({
					contacts: recipients,
					values,
					filtersData,
					filtersList,
					typeEmail,
					orgChart,
					entities,
					duplicateFilters: isDuplicateMessage ? duplicateMessage.filters : null
				});

				dispatch(setMessageLog({projectId, payload, userData: {firstName, lastName, userId}}))
					.then(handleAfterMessageLog)
					.catch((e) => setIsLoading(false));
			}
		}
	};

	if (isDuplicateMessage) props.updateInitialValues = true;

	return (
		<Form {...props}>
			{({values, errors, handleChange, handleSubmit, handleReset, isFormValid}) => {
				const hasExternal = setUserList(values).some(({isInternal}) => !isInternal);

				const renderType = () => {
					const id = TYPE;

					const list = [typeEmail, {...typeNotification, disabled: hasExternal}];

					if (hasExternal && values[id].id === typeNotification.id) {
						handleChange(id, typeEmail);
					}

					const options = list.map((item) => {
						return {...item, label: item.name};
					});

					const props = {
						id,
						name: id,
						value: values[id]?.id,
						options,
						variantHorizontal: true,
						onChange: (value) => {
							const newValue = {id: value, name: options.find((item) => item.id === value)?.name};
							return handleChange(id, newValue, {
								[SUBJECT]: '',
								[FROM_ENTITY]: null,
								[NOTIFICATION_MESSAGE]: '',
								[FROM_ENTITY_NOTIFICATION]: null,
								[FROM_ENGAGEMENT]: null,
								[LINK_LABEL]: '',
								[LINK]: ''
							});
						},
						columns: 0
					};

					return (
						<div className="message-type">
							<OutputText {...headingProps}>{labels.messageType}</OutputText>
							<RadioButtonGroup {...props} />
						</div>
					);
				};

				const sharedProps = {values, errors, handleChange, handleSubmit, handleReset, isFormValid, schema, isLoading};

				const modalProps = {
					show: showModal,
					modalsize: isDuplicateMessage ? 'large' : 'medium',
					labels: {
						modalConfirmBtnLabel: labels.generateMessage,
						modalCancelBtnLabel: labels.cancelLabel,
						modalCloseBtnTitletip: labels.closeLabel,
						modalMiddleBtnLabel: ''
					},
					headerText: labels.generateMessage,
					onOkClick: () => {
						handleSubmit(() => {
							handleReset();
							hideModal(!isDuplicateMessage);
						});
					},
					onClose: () => {
						handleReset();
						hideModal(true);
					}
				};

				if (isDuplicateMessage) {
					const goBackHandler = () => {
						handleReset();
						hideModal(false);
					};
					const closeHandler = () => {
						handleReset();
						hideModal(true);
						duplicateMessage.hideMessageLog && duplicateMessage.hideMessageLog();
					};
					modalProps.headerText = labels.duplicateMessage;
					modalProps.labels = {...modalProps.labels, modalCancelBtnLabel: labels.back};
					modalProps.onClose = closeHandler;
					modalProps.onCancelClick = goBackHandler;
				}

				return (
					<Modal {...modalProps}>
						{showModal && (
							<StyledModalContent className="customScrollbar">
								{isLoading && renderLoading()}
								<div className="instruction-text">{renderInstructionalText()}</div>
								<HrAlt $color="--neutrals-100" />
								<StyledModalGrid>
									{renderType()}
									{values[TYPE].id === typeEmail.id && <GenerateEmail {...sharedProps} />}
									{values[TYPE].id === typeNotification.id && <GenerateNotification {...sharedProps} />}
									<RecipientBulk {...sharedProps} />
								</StyledModalGrid>
							</StyledModalContent>
						)}
					</Modal>
				);
			}}
		</Form>
	);
};

GMForm.propTypes = {
	showModal: PropTypes.bool.isRequired,
	hideModal: PropTypes.func.isRequired,
	duplicateMessage: PropTypes.shape({
		values: PropTypes.shape({})
	})
};

GMForm.defaultProps = {
	duplicateMessage: null
};

export default GMForm;
